import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL } from "../lib/format";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Wrench } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export const UstaRaporu = ({ yil = 2026 }) => {
  const [ay, setAy] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil };
      if (ay !== "yil") params.ay = parseInt(ay);
      const res = await api.get("/ciro/usta-rapor", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Usta raporu yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  if (loading && !data) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  const ustalar = data?.ustalar || [];
  const maxMatrah = Math.max(...ustalar.map((u) => u.toplam_matrah), 1);

  return (
    <div className="space-y-4" data-testid="usta-raporu">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Wrench className="h-5 w-5 text-[hsl(var(--accent))]" />
            Usta Bazlı Ciro / Performans Raporu
          </h2>
          <p className="text-xs text-muted-foreground">
            Her ustanın adı geçtiği dosya sayısı ve tutarlar. "Dağıtılan Tutar" fatura girişindeki bölüm/usta kırılımından gelir; "Toplam Matrah" ustanın adı geçtiği faturaların tam tutarıdır (bölüştürülmez).
          </p>
        </div>
        <div className="ml-auto">
          <Select value={ay} onValueChange={setAy}>
            <SelectTrigger className="h-11 w-44" data-testid="usta-rapor-ay-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)}>{a} {yil}</SelectItem>
              ))}
              <SelectItem value="yil">Tüm Yıl ({yil})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {ustalar.length === 0 ? (
        <div className="rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="usta-rapor-bos">
          Bu dönemde usta atanmış kayıt bulunamadı. Yeni fatura / iş emri girerken "Bu İşte Çalışan Usta(lar)" alanından usta seçin.
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm" data-testid="usta-rapor-tablo">
              <thead className="bg-[hsl(var(--primary))] text-white">
                <tr className="text-left text-xs uppercase tracking-wide">
                  <th className="px-3 py-3 w-10">#</th>
                  <th className="px-3 py-3 min-w-[180px]">Usta</th>
                  <th className="px-3 py-3">Bölüm</th>
                  <th className="px-3 py-3 text-right">Dosya Adedi</th>
                  <th className="px-3 py-3 text-right">Dağıtılan Tutar</th>
                  <th className="px-3 py-3 text-right">Toplam Matrah</th>
                  <th className="px-3 py-3 text-right">Toplam (KDV dahil)</th>
                  <th className="px-3 py-3 min-w-[140px]">Pay</th>
                </tr>
              </thead>
              <tbody>
                {ustalar.map((u, i) => (
                  <tr key={u.id} className="border-t border-border/60" data-testid="usta-rapor-satir">
                    <td className="px-3 py-2.5 text-muted-foreground tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2.5 font-medium">{u.ad_soyad}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{u.bolum || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{u.dosya_adedi.toLocaleString("tr-TR")}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-sky-700">{u.dagitilan_tutar > 0 ? formatTL(u.dagitilan_tutar) : "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{formatTL(u.toplam_matrah)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatTL(u.toplam)}</td>
                    <td className="px-3 py-2.5">
                      <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-300"
                          style={{ width: `${Math.max((u.toplam_matrah / maxMatrah) * 100, 2)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
